import { useRef, useState } from 'react';

import { getStripe } from '@/lib/stripe';
import {
  useChangePlan,
  useInvalidateBilling,
} from '@/hooks/account/useBilling';
import { notify } from '@/lib/notify';

/**
 * `idle` → `switching` (the PATCH) → done, or → `authenticating` when the
 * switch's charge needs 3D Secure → done, or `auth_failed` (retry from there).
 */
export type PlanChangePhase =
  | 'idle'
  | 'switching'
  | 'authenticating'
  | 'auth_failed';

interface PendingAuth {
  clientSecret: string;
  /** Set after a failed attempt: Stripe detaches it, so retry passes it. */
  paymentMethodId?: string;
  successMessage: string;
}

const AUTH_UNAVAILABLE =
  'Card authentication isn’t available right now. Try again in a moment.';
const AUTH_FAILED =
  'Your bank didn’t confirm the payment, so your plan hasn’t changed.';

/**
 * Confirm a plan change, including the 3D Secure step Stripe can put in front
 * of the charge taken at the switch.
 *
 * Monthly → yearly is charged AT the switch (the interval change resets the
 * billing anchor). With Indian cards that off-session charge routinely needs
 * 3DS/OTP, and until it is confirmed Stripe keeps the change pending — the
 * user stays on the old plan. The dialog used to close with "Switched" and
 * leave it to the webhook-driven banner; now the PATCH answers
 * `requires_action` + `client_secret` and this confirms it in-page with the
 * saved card before reporting the switch.
 */
export function useConfirmPlanChange({ onDone }: { onDone: () => void }) {
  const changePlan = useChangePlan();
  const invalidateBilling = useInvalidateBilling();
  const [phase, setPhase] = useState<PlanChangePhase>('idle');
  const [authError, setAuthError] = useState<string | null>(null);
  const pending = useRef<PendingAuth | null>(null);

  // The parent's `onDone` force-refreshes the mirror (PaymentSection's
  // `pollForWebhook`), so a confirmed 3DS charge shows on the card at once.
  const finish = (successMessage: string) => {
    invalidateBilling();
    notify.success(successMessage);
    pending.current = null;
    setPhase('idle');
    onDone();
  };

  const fail = (message: string) => {
    setAuthError(message);
    setPhase('auth_failed');
  };

  const authenticate = async () => {
    const auth = pending.current;
    if (!auth) return;
    setPhase('authenticating');
    setAuthError(null);
    const stripe = await getStripe();
    if (!stripe) return fail(AUTH_UNAVAILABLE);
    const result = await stripe.confirmCardPayment(
      auth.clientSecret,
      auth.paymentMethodId ? { payment_method: auth.paymentMethodId } : {}
    );
    if (result.error) {
      const lastPm =
        result.error.payment_intent?.last_payment_error?.payment_method?.id;
      if (lastPm) auth.paymentMethodId = lastPm;
      // A failed or abandoned challenge: Stripe's own sentence ("choose a
      // different payment method") contradicts the retry, which re-opens the
      // challenge for the same saved card. Any other error keeps its message.
      const detail =
        result.error.code === 'payment_intent_authentication_failure'
          ? ''
          : ` ${result.error.message ?? ''}`;
      return fail(`${AUTH_FAILED}${detail}`.trim());
    }
    finish(auth.successMessage);
  };

  const run = (planId: string, successMessage: string) => {
    setPhase('switching');
    changePlan.mutate(planId, {
      onSuccess: response => {
        if (response.requires_action && response.client_secret) {
          pending.current = {
            clientSecret: response.client_secret,
            successMessage,
          };
          void authenticate();
          return;
        }
        finish(successMessage);
      },
      onError: err => {
        setPhase('idle');
        notify.error(err, "Couldn't change plan");
      },
    });
  };

  return {
    run,
    retry: () => void authenticate(),
    phase,
    authError,
    isPending: phase === 'switching' || phase === 'authenticating',
  };
}
